import React, { useState, useEffect } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";

const ProductVariant = () => {
  const [form, setForm] = useState({
    category_id: "",
    subcategory_id: "",
    product_id: "",
    variant_name: "",
    class_id: "",
    height_range: "",
    weight_range: "",
    size: "",
    cost_price: "",
    sale_price: "",
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [classes, setClasses] = useState([]);
  const [editId, setEditId] = useState(null);
  const schoolId = localStorage.getItem("school_id");

  const categoryURL = `https://dgps.co.in/api/product/category/list?school_id=${schoolId}`;
  const subcategoryURL = `https://dgps.co.in/api/product/subcategory/list`;
  const productURL = `https://dgps.co.in/api/product/list`;
  const baseURL = "https://dgps.co.in/api/product/variant";
  const addURL = `${baseURL}/add`;
  const updateURL = `${baseURL}/update`;
  const deleteURL = `${baseURL}/delete`;

  // ✅ Fetch classes
  useEffect(() => {
    fetch("https://dgps.co.in/api/get-classes")
      .then((res) => res.json())
      .then((data) => setClasses(data.data || []))
      .catch((error) => console.error("Error fetching classes:", error));
  }, []);

  // ✅ Fetch categories
  const fetchCategories = async () => {
    try {
      const res = await fetch(categoryURL);
      const data = await res.json();
      setCategories(data || []);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  // ✅ Fetch subcategories by category
  const fetchSubcategories = async (categoryId) => {
    if (!categoryId) return setSubcategories([]);
    try {
      const res = await fetch(`${subcategoryURL}?category_id=${categoryId}`);
      const data = await res.json();
      setSubcategories(data || []);
    } catch (err) {
      console.error("Error fetching subcategories:", err);
    }
  };

  // ✅ Fetch products by subcategory
  const fetchProducts = async (subcategoryId) => {
    if (!subcategoryId) return setProducts([]);
    try {
      const res = await fetch(
        `${productURL}?school_id=${schoolId}&subcategory_id=${subcategoryId}`
      );
      const data = await res.json();
      setProducts(data || []);
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  };

  // ✅ Fetch variants by product
  const fetchVariants = async (productId) => {
    if (!productId) return setVariants([]);
    try {
      const res = await fetch(
        `${baseURL}/list?school_id=${schoolId}&product_id=${productId}`
      );
      const data = await res.json();
      setVariants(data || []);
    } catch (err) {
      console.error("Error fetching variants:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // ✅ Handle input changes
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Handle dropdown cascading
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setForm({ ...form, category_id: categoryId, subcategory_id: "", product_id: "" });
    fetchSubcategories(categoryId);
    setSubcategories([]);
    setProducts([]);
    setVariants([]);
  };

  const handleSubcategoryChange = (e) => {
    const subId = e.target.value;
    setForm({ ...form, subcategory_id: subId, product_id: "" });
    fetchProducts(subId);
    setProducts([]);
    setVariants([]);
  };

  const handleProductChange = (e) => {
    const productId = e.target.value;
    setForm({ ...form, product_id: productId });
    fetchVariants(productId);
  };

  // ✅ Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, school_id: schoolId };

    try {
      if (editId) {
        payload.id = editId;
        await fetch(updateURL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await fetch(addURL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      setForm({
        category_id: "",
        subcategory_id: "",
        product_id: "",
        variant_name: "",
        class_id: "",
        height_range: "",
        weight_range: "",
        size: "",
        cost_price: "",
        sale_price: "",
      });
      setEditId(null);
      fetchVariants(payload.product_id);
    } catch (err) {
      console.error("Error saving variant", err);
    }
  };

  // ✅ Edit
  const handleEdit = (item) => {
    setForm({
      product_id: item.product_id,
      variant_name: item.variant_name,
      class_id: item.class_id,
      height_range: item.height_range,
      weight_range: item.weight_range,
      size: item.size,
      cost_price: item.cost_price,
      sale_price: item.sale_price,
    });
    setEditId(item.id);
    fetchVariants(item.product_id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ✅ Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this variant?")) return;
    try {
      await fetch(deleteURL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      fetchVariants(form.product_id);
    } catch (err) {
      console.error("Delete error", err);
    }
  };

  // ✅ Utility: product type detect
  const selectedProductType = products.find((p) => p.id === form.product_id)?.product_type;

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      <div style={{ flex: 1, backgroundColor: "#f4f6f8" }}>
        <main style={{ padding: "30px", maxWidth: "1000px", margin: "auto", marginTop: "0px" }}>
          {/* Add/Edit Variant Section */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>{editId ? "✏️ Edit Variant" : "➕ Add New Variant"}</h2>
            <form onSubmit={handleSubmit}>
              <div style={formGrid}>
                {/* Category */}
                <FormGroup label="Category">
                  <select
                    name="category_id"
                    value={form.category_id || ""}
                    onChange={handleCategoryChange}
                    style={fullWidthInput}
                    required
                  >
                    <option value="">-- Select Category --</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                {/* Subcategory */}
                <FormGroup label="Subcategory">
                  <select
                    name="subcategory_id"
                    value={form.subcategory_id || ""}
                    onChange={handleSubcategoryChange}
                    style={fullWidthInput}
                    required
                  >
                    <option value="">-- Select Subcategory --</option>
                    {subcategories.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.subcategory_name}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                {/* Product */}
                <FormGroup label="Product">
                  <select
                    name="product_id"
                    value={form.product_id || ""}
                    onChange={handleProductChange}
                    style={fullWidthInput}
                    required
                  >
                    <option value="">-- Select Product --</option>
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.product_name}
                      </option>
                    ))}
                  </select>
                </FormGroup>

                {/* Variant Name */}
                <FormGroup label="Variant Name">
                  <input
                    type="text"
                    name="variant_name"
                    value={form.variant_name}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>

                {/* ✅ Conditional Fields */}
                {selectedProductType === "book" && (
                  <FormGroup label="Select Class">
                    <select
                      name="class_id"
                      value={form.class_id}
                      onChange={handleChange}
                      style={fullWidthInput}
                    >
                      <option value="">-- Select Class --</option>
                      {classes.map((cls) => (
                        <option key={cls.id} value={cls.id}>
                          {cls.class_name}
                        </option>
                      ))}
                    </select>
                  </FormGroup>
                )}

                {selectedProductType === "dress" && (
                  <>
                    <FormGroup label="Height Range (if dress)">
                      <input
                        type="text"
                        name="height_range"
                        value={form.height_range}
                        onChange={handleChange}
                        style={fullWidthInput}
                      />
                    </FormGroup>

                    <FormGroup label="Weight Range (if dress)">
                      <input
                        type="text"
                        name="weight_range"
                        value={form.weight_range}
                        onChange={handleChange}
                        style={fullWidthInput}
                      />
                    </FormGroup>
                  </>
                )}

                {selectedProductType === "shoes" && (
                  <FormGroup label="Size (if shoes)">
                    <input
                      type="text"
                      name="size"
                      value={form.size}
                      onChange={handleChange}
                      style={fullWidthInput}
                    />
                  </FormGroup>
                )}

                {/* Prices */}
                <FormGroup label="Cost Price">
                  <input
                    type="number"
                    name="cost_price"
                    value={form.cost_price}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>

                <FormGroup label="Sale Price">
                  <input
                    type="number"
                    name="sale_price"
                    value={form.sale_price}
                    onChange={handleChange}
                    style={fullWidthInput}
                    required
                  />
                </FormGroup>
              </div>

              <div style={{ textAlign: "right", marginTop: "20px" }}>
                <button type="submit" style={buttonStyle}>
                  {editId ? "Update Variant" : "Add Variant"}
                </button>
              </div>
            </form>
          </section>

          {/* Variant List */}
          <section style={cardStyle}>
            <h2 style={titleStyle}>📋 All Variants</h2>
            {variants.length === 0 ? (
              <p>No variants found.</p>
            ) : (
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {variants.map((item, idx) => (
                  <li
                    key={item.id}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      borderRadius: "8px",
                      background: idx % 2 === 0 ? "#f9f9f9" : "#eef3f9",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: "16px", color: "#333" }}>
                        {item.variant_name}
                      </strong>
                      <div style={{ color: "#777", fontSize: "13px" }}>
                        Cost: ₹{item.cost_price} | Sale: ₹{item.sale_price}
                      </div>
                    </div>
                    <div>
                      <FaEdit
                        onClick={() => handleEdit(item)}
                        style={{
                          color: "#0077cc",
                          cursor: "pointer",
                          marginRight: "15px",
                        }}
                      />
                      <FaTrash
                        onClick={() => handleDelete(item.id)}
                        style={{ color: "red", cursor: "pointer" }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

// Reusable Form Group
const formGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "20px",
};

const fullWidthInput = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  fontSize: "15px",
  boxSizing: "border-box",
};

const FormGroup = ({ label, children, style }) => (
  <div style={{ ...style }}>
    <label
      style={{
        marginBottom: "6px",
        display: "block",
        fontWeight: 500,
        color: "#444",
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

const buttonStyle = {
  padding: "10px 25px",
  backgroundColor: "#0077cc",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "15px",
  cursor: "pointer",
};

const cardStyle = {
  background: "#ffffff",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
  marginBottom: "30px",
};

const titleStyle = {
  color: "#0077cc",
  marginBottom: "25px",
};

export default ProductVariant;
